import React from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon.js';

import style from './ListingConfig.module.css';

import {
	buildTestData,
} from 'Vulture/TestData.js';
import NewListingModal from './NewListingModal.js';

import {
	providerListToColumnMap,
} from './listingIntegrationModels.js';

import {
	fetchListingColumnTypes,
} from 'Vulture/Listing.js';

import {
	ListingConfigTier,
} from './ListingConfigTier.js';

import {
	ListingConfigContext,
} from './ListingConfigContext.js';

class ListingConfig extends React.Component {
	static contextType = ListingConfigContext;

	constructor(props) {
		super(props);

		this.state = {
			listingColumnTypeOptions: [],
			searchTerm:               '',
			expandedAccordions:       {},
		};

		// Per-tier accordion DOM nodes, keyed by listing_config UUID. Set
		// via the ref callback on each accordion wrapper; consumed by
		// focusListingConfig to scroll the newly-created list into view.
		this.accordionRefs = new Map();
	}

	setAccordionRef = (uuid) => {
		return (node) => {
			if (node) {
				this.accordionRefs.set(uuid, node);
			} else {
				this.accordionRefs.delete(uuid);
			}
		};
	};

	async componentDidMount() {
		await this.fetchListingColumnTypes();
	}

	async fetchListingColumnTypes() {
		try {
			const contents = await fetchListingColumnTypes();

			if (contents) {
				let listingColumnTypeOptions = [];
				if (contents?.data) {
					contents.data.forEach((listing_column_type) => {
						const {
							display_name,
							uuid,
						} = listing_column_type;

						listingColumnTypeOptions.push({
							label: display_name,
							value: uuid,
						});
					});
				}

				this.setState({
					listingColumnTypeOptions,
				});
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('ListingConfig: Failed to fetch listing column types. New configurations may not work properly.', error);
		}
	}

	handleMakeNewListingConfig(parentM1, parentM1UUID, shouldScroll = true) {
		const {
			createListingConfigTree,
		} = this.context;

		const fields = {
			content_group_count: 1,
			data_width:          80,
			display_name:        '',
		};
		if (parentM1 === 'account') {
			fields.use_all_listings = true;
		}

		const newListingConfigUUID = createListingConfigTree(parentM1, parentM1UUID, [
			{
				fields,
			},
		]);

		if (shouldScroll && newListingConfigUUID) {
			setTimeout(() => {
				this.focusListingConfig(newListingConfigUUID);
			}, 100);
		}
	}

	handleImportListingConfig(parentM1, parentM1UUID, dataSetUUID) {
		const {
			dataSets,
		} = this.props;

		const {
			createListingConfigTree,
		} = this.context;

		const rawTiers = JSON.parse(dataSets[dataSetUUID].json).tiers;

		const tiers = rawTiers.map((rawTier, i) => {
			const fields = {
				content_group_count: 1,
				data_width:          80,
				display_name:        rawTier.listName,
			};
			if (i === 0 && parentM1 === 'account') {
				fields.use_all_listings = true;
			}
			return {
				fields,
				columns: (rawTier.data || []).map((datum) => {
					return {
						display_name:             datum.label,
						listing_column_type_uuid: datum.type.value,
					};
				}),
			};
		});

		const rootUUID = createListingConfigTree(parentM1, parentM1UUID, tiers);

		if (rootUUID) {
			setTimeout(() => {
				this.focusListingConfig(rootUUID);
			}, 100);
		}
	}

	handleIntegrateListingConfig(parentM1, parentM1UUID, integrationProvider, selectedIntegrationList) {
		const {
			settingMap,
		} = this.props;

		const {
			listingColumnTypeOptions,
		} = this.state;

		const {
			createListingConfigTree,
		} = this.context;

		const providerUUID = settingMap['listing_integration_provider'].uuid;
		const providerListUUID = settingMap['listing_integration_list'].uuid;

		const fields = {
			content_group_count: 1,
			data_width:          80,
			display_name:        selectedIntegrationList,
		};
		if (parentM1 === 'account') {
			fields.use_all_listings = true;
		}

		const settings = {
			[providerUUID]: {
				string: integrationProvider.toLowerCase(),
			},
			[providerListUUID]: {
				string: selectedIntegrationList.toLowerCase(),
			},
		};

		const columnsToBuild = providerListToColumnMap[integrationProvider][selectedIntegrationList];
		const columns = Object.entries(columnsToBuild).map(([ column, columnType, ]) => {
			const listing_column_type_uuid = listingColumnTypeOptions.find((opt) => {
				return opt.label === columnType;
			})?.value;
			return {
				display_name: column,
				listing_column_type_uuid,
			};
		});

		const rootUUID = createListingConfigTree(parentM1, parentM1UUID, [
			{
				fields,
				columns,
				settings,
			},
		]);

		if (rootUUID) {
			setTimeout(() => {
				this.focusListingConfig(rootUUID);
			}, 100);
		}
	}

	openNewListingModal(parentM1, parentM1UUID) {
		const {
			app,
			dataSets,
		} = this.props;

		app.openModal({
			modalSize:  4,
			showModal:  true,
			modalProps: {
				title: 'New Listing',
				jsx:   (
					<NewListingModal
						app={this.props.app}
						dataSets={dataSets}
						newListing={() => {
							const shouldScroll = parentM1 === 'account';
							this.handleMakeNewListingConfig(parentM1, parentM1UUID, shouldScroll);
						}}
						importListing={(selectedDataSet) => {
							this.handleImportListingConfig(parentM1, parentM1UUID, selectedDataSet);
						}}
						integrateListing={(selectedIntegration, selectedIntegrationList) => {
							this.handleIntegrateListingConfig(parentM1, parentM1UUID, selectedIntegration, selectedIntegrationList);
						}}
						closeModal={() => {
							app.setState({
								modal:      false,
								modalProps: {},
							});
						}}
					/>
				),
			},
		});
	}

	findChildEntitiesForRender(m1, parentUUID) {
		const {
			listingConfigMap,
		} = this.context;

		if (!parentUUID || typeof parentUUID !== 'string') {
			return [];
		}

		if (!listingConfigMap || typeof listingConfigMap !== 'object') {
			return [];
		}

		let childEntities = [];

		try {
			const parentConfig = listingConfigMap[parentUUID];

			if (parentConfig && typeof parentConfig === 'object' && parentConfig.childTiers) {
				if (Array.isArray(parentConfig.childTiers)) {
					for (let childUUID of parentConfig.childTiers) {
						try {
							if (childUUID && typeof childUUID === 'string') {
								const childData = listingConfigMap[childUUID];

								childEntities.push({
									uuid: childUUID,
									data: childData || null,
								});
							}
						} catch (error) {
							// Skip problematic child entities
						}
					}
				}
			}
		} catch (error) {
			return [];
		}

		return childEntities;
	}

	buildTierHierarchy() {
		const {
			listingConfigMap,
			rootOrder,
		} = this.context;

		if (!listingConfigMap || typeof listingConfigMap !== 'object') {
			return [];
		}

		// rootOrder is the canonical render order — alphabetized once per
		// fetch, appended on create, spliced on delete, untouched on rename.
		// Any root in the map that isn't in the list (safety net for a
		// stale/desynced order) is appended in map iteration order so
		// nothing silently disappears.
		const orderedRootUUIDs = Array.isArray(rootOrder) ? [ ...rootOrder, ] : [];
		const seen = new Set(orderedRootUUIDs);
		for (const [ uuid, data, ] of Object.entries(listingConfigMap)) {
			if (data && data.root && !seen.has(uuid)) {
				orderedRootUUIDs.push(uuid);
				seen.add(uuid);
			}
		}

		const rootConfigurations = orderedRootUUIDs
			.filter((uuid) => {
				return uuid && typeof uuid === 'string' && listingConfigMap?.[uuid]?.root;
			})
			.map((uuid) => {
				return {
					uuid,
					config: listingConfigMap[uuid],
				};
			});

		return rootConfigurations.map((rootConfig) => {
			const buildTierTree = (configUUID, level = 1) => {
				if (!configUUID || typeof configUUID !== 'string') {
					return null;
				}

				if (!level || typeof level !== 'number' || level < 1) {
					level = 1;
				}

				const config = listingConfigMap?.[configUUID];
				if (!config || typeof config !== 'object') {
					return null;
				}

				let children = [];
				try {
					if (config.childTiers && Array.isArray(config.childTiers)) {
						children = config.childTiers.filter((childUUID) => {
							return childUUID && typeof childUUID === 'string';
						});
					}
				} catch (error) {
					children = [];
				}

				return {
					uuid:     configUUID,
					level,
					config,
					children: children.map((childUUID) => {
						try {
							return buildTierTree(childUUID, level + 1);
						} catch (error) {
							return null;
						}
					}).filter(Boolean),
				};
			};

			try {
				return buildTierTree(rootConfig?.uuid);
			} catch (error) {
				return null;
			}
		}).filter(Boolean);
	}

	renderTierHierarchy(tierTree, listNumber) {
		const {
			app,
			accountUUID,
			settingMap,
		} = this.props;

		const {
			listingConfigMap,
		} = this.context;

		// Flatten the hierarchy into horizontal tiers
		const getAllTierLevels = (node, levels = {}) => {
			if (!node || typeof node !== 'object') {
				return levels || {};
			}

			if (!levels || typeof levels !== 'object') {
				levels = {};
			}

			try {
				const level = node.level;

				if (typeof level === 'number' && level > 0) {
					if (!levels[level]) levels[level] = [];
					levels[level].push(node);
				}

				if (node.children && Array.isArray(node.children) && node.children.length > 0) {
					node.children.forEach((child) => {
						try {
							return getAllTierLevels(child, levels);
						} catch (error) {
							// Skip problematic children
						}
					});
				}
			} catch (error) {
				// Return current levels if error occurs
			}

			return levels;
		};

		const tierLevels = getAllTierLevels(tierTree);
		const maxLevel = Math.max(...Object.keys(tierLevels).map(Number));
		const accordionId = `accordion-${tierTree.uuid}`;
		const isExpanded = this.state.expandedAccordions[accordionId] === true;

		let rootConfigName = `List ${listNumber}`;
		try {
			if (tierTree?.config?.display_name && typeof tierTree.config.display_name === 'string') {
				rootConfigName = tierTree.config.display_name;
			}
		} catch (error) {
			// Keep default name if access fails
		}

		// Generate breadcrumb content for when accordion is collapsed
		const getBreadcrumbContent = () => {
			if (isExpanded) return null;

			let tierCount = 0;
			try {
				tierCount = typeof maxLevel === 'number' && maxLevel > 0 ? maxLevel : 0;
			} catch (error) {
				tierCount = 0;
			}

			let totalColumns = 0;

			if (!listingConfigMap || typeof listingConfigMap !== 'object') {
				return `${tierCount} tier${tierCount !== 1 ? 's' : ''} • 0 columns`;
			}

			const countColumnsInNode = (node) => {
				if (!node || typeof node !== 'object' || !node.uuid || typeof node.uuid !== 'string') {
					return;
				}

				try {
					const configData = listingConfigMap[node.uuid];

					if (configData && typeof configData === 'object') {
						for (const [ m1Key, m1Data, ] of Object.entries(configData)) {
							try {
								if (m1Key && typeof m1Key === 'string' && m1Key.startsWith('listing_column.') && m1Data) {
									totalColumns++;
								}
							} catch (error) {
								// Skip problematic entries
							}
						}
					}
				} catch (error) {
					// Skip if config access fails
				}

				try {
					if (node.children && Array.isArray(node.children)) {
						node.children.forEach((child) => {
							try {
								return countColumnsInNode(child);
							} catch (error) {
								// eslint-disable-next-line no-console
								console.error('ListingConfig: Skipped child due to error:', error);
							}
						});
					}
				} catch (error) {
					// Skip if children access fails
				}
			};

			try {
				countColumnsInNode(tierTree);
			} catch (error) {
				// Keep totalColumns as 0 if counting fails
			}

			return `${tierCount} tier${tierCount !== 1 ? 's' : ''} • ${totalColumns} column${totalColumns !== 1 ? 's' : ''}`;
		};

		return (
			<div className={style.accordionWrapper} ref={this.setAccordionRef(tierTree.uuid)} data-test-id={`listConfig-${tierTree.uuid}`}>
				<div
					className={`${style.accordionHeader} ${isExpanded ? style.accordionHeaderExpanded : ''}`}
					data-test-id={`accordionHeader-${tierTree.uuid}`}
					onClick={() => {
						return this.toggleAccordion(accordionId);
					}}
				>
					<div className={style.accordionHeaderContent}>
						<div className={style.accordionHeaderText}>
							<h3 className={style.accordionTitle} data-test-id={`accordionTitle-${tierTree.uuid}`}>{rootConfigName}</h3>
							{getBreadcrumbContent() && (
								<span className={style.accordionBreadcrumb}>
									{getBreadcrumbContent()}
								</span>
							)}
						</div>
						<div className={style.accordionIcon}>
							<CustomIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
						</div>
					</div>
				</div>

				<div className={`${style.accordionContent} ${isExpanded ? style.accordionContentExpanded : style.accordionContentCollapsed}`}>
					<div className={style.tierHierarchy}>
						<div className={style.tierHorizontalContainer}>
							{Array.from({
								length: maxLevel,
							}, (_, i) => {
								return i + 1;
							}).map((level) => {
								const nodesAtLevel = tierLevels[level] || [];
								if (nodesAtLevel.length === 0) return null;

								return (
									<div key={`tier-${level}`} className={style.tierColumn}>
										<div className={style.tierColumnNodes}>
											{nodesAtLevel.map((node) => {
												return (
													<div key={node.uuid} className={style.tierNodeWrapper}>
														<ListingConfigTier
															app={app}
															accountUUID={accountUUID}
															listingConfigUUID={node.uuid}
															settingMap={settingMap}
															listNumber={listNumber}
															listingColumnTypeOptions={this.state.listingColumnTypeOptions}
															pendingChanges={this.props.pendingChanges}
															messageLabels={this.props.messageLabels}
															arrayOptions={this.props.arrayOptions}
															tier={level}
															hideChildRendering={true}
															openNewListingModal={this.openNewListingModal.bind(this)}
															findChildEntitiesForRender={this.findChildEntitiesForRender.bind(this)}
															handleMakeNewListingConfig={(parentM1, parentM1UUID) => {
																return this.handleMakeNewListingConfig(parentM1, parentM1UUID, false);
															}}
														/>
													</div>
												);
											})}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		);
	}

	focusListingConfig(newListingConfigUUID) {
		// Scroll to the new accordion's element, not `scrollHeight`. The
		// new tier has an empty display_name → sorts as "List N" near the
		// top, so the previous `scrollTo(scrollHeight)` landed the user
		// on whatever list was at the bottom and any `+ Data` click hit
		// the wrong tier.
		const accordionId = `accordion-${newListingConfigUUID}`;
		this.setState((prevState) => {
			return {
				expandedAccordions: {
					...prevState.expandedAccordions,
					[accordionId]: true,
				},
			};
		}, () => {
			const el = this.accordionRefs.get(newListingConfigUUID);
			if (el) {
				el.scrollIntoView({
					behavior: 'smooth',
					block:    'start',
				});
			}
		});
	}

	handleSearchChange(e) {
		this.setState({
			searchTerm: e.target.value,
		});
	}

	toggleAccordion(accordionId) {
		this.setState((prevState) => {
			return {
				expandedAccordions: {
					...prevState.expandedAccordions,
					[accordionId]: !prevState.expandedAccordions[accordionId],
				},
			};
		});
	}

	filterTierHierarchies(tierHierarchies) {
		const {
			searchTerm,
		} = this.state;

		if (!tierHierarchies || !Array.isArray(tierHierarchies)) {
			return [];
		}

		if (!searchTerm || typeof searchTerm !== 'string' || !searchTerm.trim()) {
			return tierHierarchies;
		}

		let searchLower = '';
		try {
			searchLower = searchTerm.toLowerCase();
		} catch (error) {
			// If toLowerCase fails, return unfiltered results
			return tierHierarchies;
		}

		// Filter the tier hierarchies based on search term with defensive checks
		const filteredHierarchies = tierHierarchies.filter((tierTree) => {
			if (!tierTree || typeof tierTree !== 'object') {
				return false;
			}

			try {
				const rootConfig = tierTree.config;
				let rootName = '';

				try {
					rootName = (rootConfig?.display_name ?? '').toString();
				} catch (error) {
					rootName = '';
				}

				if (rootName && rootName.toLowerCase().includes(searchLower)) {
					return true;
				}

				const checkChildren = (node) => {
					if (!node || typeof node !== 'object') {
						return false;
					}

					try {
						let displayName = '';
						try {
							displayName = (node.config?.display_name ?? '').toString();
						} catch (error) {
							displayName = '';
						}

						if (displayName && displayName.toLowerCase().includes(searchLower)) {
							return true;
						}

						if (node.children && Array.isArray(node.children)) {
							return node.children.some((child) => {
								try {
									return checkChildren(child);
								} catch (error) {
									return false;
								}
							});
						}

						return false;
					} catch (error) {
						return false;
					}
				};

				return checkChildren(tierTree);
			} catch (error) {
				// If any error occurs during filtering, exclude this item
				return false;
			}
		});

		// filteredHierarchies is already in rootOrder (buildTierHierarchy
		// iterates it). Filtering preserves that order — no resort here.
		return filteredHierarchies.filter((tierTree) => {
			return tierTree && typeof tierTree === 'object';
		});
	}

	renderListingConfigs() {
		let tierHierarchies = [];
		let filteredHierarchies = [];

		try {
			tierHierarchies = this.buildTierHierarchy();
		} catch (error) {
			tierHierarchies = [];
		}

		try {
			filteredHierarchies = this.filterTierHierarchies(tierHierarchies);
		} catch (error) {
			filteredHierarchies = tierHierarchies || [];
		}

		if (!Array.isArray(filteredHierarchies)) {
			filteredHierarchies = [];
		}

		let listNumber = 0;

		let searchTerm = '';
		try {
			searchTerm = this.state?.searchTerm || '';
		} catch (error) {
			searchTerm = '';
		}

		if (filteredHierarchies.length === 0 && searchTerm.trim()) {
			return (
				<div className={style.noResultsMessage}>
					<p>No listing configurations found matching &ldquo;{searchTerm}&rdquo;</p>
				</div>
			);
		}

		return (
			<div className={style.listingConfigsContainer}>
				{filteredHierarchies.map((tierTree) => {
					if (!tierTree || typeof tierTree !== 'object' || !tierTree.uuid) {
						return null;
					}

					listNumber++;

					try {
						return (
							<div
								className={style.listingContainer}
								key={tierTree.uuid}
							>
								{this.renderTierHierarchy(tierTree, listNumber)}
							</div>
						);
					} catch (error) {
						// Skip problematic tier trees
						return null;
					}
				}).filter(Boolean)} {/* Filter out null entries */}
			</div>
		);
	}

	render() {
		const {
			accountUUID,
		} = this.props;

		return (
			<div className={style.listingConfigWrapper}>
				<div className={style.listingConfigHeader}>
					<button
						className={`button ${style.button}`}
						onClick={(e) => {
							e.preventDefault();
							this.openNewListingModal('account', accountUUID);
						}}
						data-testing-info={buildTestData('button--newList')}
						data-test-id='listingConfigNewListButton'
					>
						<CustomIcon icon='plus' /> New List Configuration
					</button>

					<div className={style.searchContainer}>
						<input
							type="text"
							placeholder="Search by list name..."
							value={this.state.searchTerm}
							onChange={this.handleSearchChange.bind(this)}
							className={style.searchInput}
						/>
						<CustomIcon icon='search' className={style.searchIcon} />
					</div>
				</div>

				{this.renderListingConfigs()}
			</div>
		);
	}
}

export default ListingConfig;